/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DocIcon,
  CloudUpload as CloudUploadIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import '../../styles/ProjectDocuments.css';

const getFileIcon = (contentType) => {
  if (!contentType) return <FileIcon />;
  if (contentType === 'application/pdf') return <PdfIcon sx={{ color: '#e53935' }} />;
  if (contentType.startsWith('image/')) return <ImageIcon sx={{ color: '#43a047' }} />;
  if (contentType.includes('word')) return <DocIcon sx={{ color: '#1565c0' }} />;
  return <FileIcon sx={{ color: '#757575' }} />;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getUploaderName = (doc) => {
  const u = doc.uploaded_by;
  if (!u) return 'Unknown';
  if (typeof u === 'string') return u;
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ');
  return name || u.email || 'Unknown';
};

const isViewable = (contentType) => {
  if (!contentType) return false;
  return contentType === 'application/pdf' || contentType.startsWith('image/');
};

const ProjectDocuments = ({ projectId, user }) => {
  const [documents, setDocuments] = useState([]);
  const [maxDocuments, setMaxDocuments] = useState(10);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [label, setLabel] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await axios.get(`/api/projects/${projectId}/documents`);
      setDocuments(res.data.documents);
      setMaxDocuments(res.data.maxDocuments);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('document', selectedFile);
    formData.append('label', label.trim());

    try {
      await axios.post(`/api/projects/${projectId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setLabel('');
      fetchDocuments();
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to upload document';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleView = async (doc) => {
    try {
      const res = await axios.get(
        `/api/projects/${projectId}/documents/${doc._id}/view`,
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: doc.file_data?.contentType || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to view document');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(
        `/api/projects/${projectId}/documents/${doc._id}/download`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDelete = async () => {
    if (!docToDelete) return;
    try {
      await axios.delete(`/api/projects/${projectId}/documents/${docToDelete._id}`);
      toast.success('Document deleted');
      setDeleteDialogOpen(false);
      setDocToDelete(null);
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const openUploadDialog = () => {
    setSelectedFile(null);
    setLabel('');
    setUploadDialogOpen(true);
  };

  const openDeleteDialog = (doc) => {
    setDocToDelete(doc);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <Box className="proj-docs-loading">
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">Loading documents...</Typography>
      </Box>
    );
  }

  const limitReached = documents.length >= maxDocuments;

  return (
    <Box className="proj-docs-container">
      <div className="proj-docs-header">
        <div className="proj-docs-header-left">
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Project Documents
          </Typography>
          <Chip
            label={`${documents.length} / ${maxDocuments}`}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.75rem', height: 24 }}
          />
        </div>
        <Button
          variant="contained"
          size="small"
          startIcon={<UploadIcon />}
          onClick={openUploadDialog}
          disabled={limitReached}
          sx={{
            textTransform: 'none',
            fontSize: '0.8rem',
            borderRadius: '6px',
            backgroundColor: '#475569',
            '&:hover': { backgroundColor: '#334155' },
          }}
        >
          Upload
        </Button>
      </div>

      {limitReached && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 1, display: 'block' }}>
          Maximum document limit reached ({maxDocuments}).
        </Typography>
      )}

      {documents.length === 0 ? (
        <div className="proj-docs-empty">
          <FileIcon sx={{ fontSize: 40, color: '#cbd5e1' }} />
          <Typography variant="body2" color="text.secondary">
            No documents uploaded yet
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Upload project contracts, reports, or other files
          </Typography>
        </div>
      ) : (
        <div className="proj-docs-list">
          {documents.map((doc) => (
            <div key={doc._id} className="proj-docs-item">
              <div className="proj-docs-item-icon">
                {getFileIcon(doc.file_data?.contentType)}
              </div>
              <div className="proj-docs-item-info">
                <span className="proj-docs-item-name">{doc.original_filename}</span>
                {doc.label && (
                  <span className="proj-docs-item-label">{doc.label}</span>
                )}
                <span className="proj-docs-item-meta">
                  {formatFileSize(doc.file_size)} &middot; {getUploaderName(doc)} &middot; {formatDate(doc.createdAt)}
                </span>
              </div>
              <div className="proj-docs-item-actions">
                {isViewable(doc.file_data?.contentType) && (
                  <Tooltip title="View">
                    <IconButton size="small" onClick={() => handleView(doc)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Download">
                  <IconButton size="small" onClick={() => handleDownload(doc)}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => openDeleteDialog(doc)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => !uploading && setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Upload Document</DialogTitle>
        <DialogContent>
          <div
            className={`proj-docs-dropzone ${dragActive ? 'active' : ''} ${selectedFile ? 'has-file' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            {selectedFile ? (
              <div className="proj-docs-dropzone-selected">
                {getFileIcon(selectedFile.type)}
                <div>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedFile.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{formatFileSize(selectedFile.size)}</Typography>
                </div>
              </div>
            ) : (
              <>
                <CloudUploadIcon sx={{ fontSize: 36, color: '#94a3b8' }} />
                <Typography variant="body2" color="text.secondary">
                  Drag & drop a file here, or click to browse
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  PDF, PNG, JPG, DOC, DOCX &middot; Max 10MB
                </Typography>
              </>
            )}
          </div>

          <TextField
            label="Label (optional)"
            placeholder="e.g., Project Contract, Safety Report"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
            size="small"
            sx={{ mt: 2 }}
            disabled={uploading}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
            sx={{
              textTransform: 'none',
              backgroundColor: '#475569',
              '&:hover': { backgroundColor: '#334155' },
            }}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs">
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete <strong>{docToDelete?.original_filename}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectDocuments;
